import { useState, useEffect } from 'react'
import { X, CheckCircle, XCircle, Edit2, Trash2, Clock, MapPin, Move, Camera } from 'lucide-react'
import { UserPOI } from '../../types/userPois'
import { getUserPOITypeById } from '../../data/userPoiTypes'
import { UserPOICommentsList } from './UserPOICommentsList'
import { UserPOIVoteHistory } from './UserPOIVoteHistory'
import { useUserPOIComments } from '../../hooks/useUserPOIComments'
import { uploadUserPOIPhoto, deleteUserPOIPhoto } from '../../services/firebase/storage'

interface UserPOIDetailModalProps {
  poi: UserPOI | null
  isOpen: boolean
  onClose: () => void
  onConfirm?: (poiId: string) => Promise<void>
  onMarkOutdated?: (poiId: string) => Promise<void>
  onRemoveVote?: (poiId: string) => Promise<void>
  onEdit?: (poiId: string, name: string, description: string, photos?: string[]) => Promise<void>
  onDelete?: (poiId: string) => Promise<void>
  onResolve?: (poiId: string, resolved: boolean) => Promise<void>
  onStartMove?: (poiId: string) => void
  currentUserId?: string
  currentUserName?: string
  currentUserPhoto?: string
  embedMode?: boolean
}

export function UserPOIDetailModal({
  poi,
  isOpen,
  onClose,
  onConfirm,
  onMarkOutdated,
  onRemoveVote,
  onEdit,
  onDelete,
  onResolve,
  onStartMove,
  currentUserId,
  currentUserName,
  currentUserPhoto,
  embedMode = false
}: UserPOIDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editPhotos, setEditPhotos] = useState<string[]>([])
  const [newPhotos, setNewPhotos] = useState<File[]>([])
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [viewportScale, setViewportScale] = useState(1)

  // Comments hook
  const {
    comments,
    loading: commentsLoading,
    addComment,
    editComment,
    removeComment
  } = useUserPOIComments({
    poiId: poi?.id || null,
    userId: currentUserId,
    userDisplayName: currentUserName,
    userPhotoURL: currentUserPhoto
  })

  // Update viewport scale based on window size
  useEffect(() => {
    const updateScale = () => {
      let scale = 1
      if (window.innerWidth < 640) scale = 0.65
      else if (window.innerWidth < 768) scale = 0.75
      else if (window.innerWidth < 1024) scale = 0.85
      
      if (embedMode) scale = scale * 0.7
      setViewportScale(scale)
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [embedMode])

  // Reset edit state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false)
      setEditName('')
      setEditDescription('')
      setEditPhotos([])
      setNewPhotos([])
      setNewPhotoPreviews([])
    }
  }, [isOpen])

  if (!isOpen || !poi) return null

  const poiType = getUserPOITypeById(poi.type)
  if (!poiType) return null

  const Icon = poiType.icon
  const isAuthor = currentUserId === poi.authorId
  const hasConfirmed = currentUserId && poi.confirmations && currentUserId in poi.confirmations
  const hasMarkedOutdated = currentUserId && poi.outdatedVotes && currentUserId in poi.outdatedVotes
  const hasVoted = hasConfirmed || hasMarkedOutdated

  const confirmationCount = Object.keys(poi.confirmations || {}).length
  const outdatedCount = Object.keys(poi.outdatedVotes || {}).length
  const totalVotes = confirmationCount + outdatedCount

  const handleEditSubmit = async () => {
    if (!onEdit || !editName.trim() || !editDescription.trim()) return

    setIsSubmitting(true)
    try {
      // Upload new photos
      let uploadedPhotoUrls: string[] = []
      if (newPhotos.length > 0) {
        const uploadPromises = newPhotos.map(photo => 
          uploadUserPOIPhoto(currentUserId || '', poi.id, photo)
        )
        uploadedPhotoUrls = await Promise.all(uploadPromises)
      }

      // Combine existing photos with new ones
      const finalPhotos = [...editPhotos, ...uploadedPhotoUrls]

      // Delete removed photos from storage
      if (poi.photos) {
        const removedPhotos = poi.photos.filter(photo => !editPhotos.includes(photo))
        for (const photoUrl of removedPhotos) {
          try {
            await deleteUserPOIPhoto(photoUrl)
          } catch (error) {
            console.error('Failed to delete photo:', error)
          }
        }
      }

      await onEdit(poi.id, editName.trim(), editDescription.trim(), finalPhotos)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to edit POI:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddNewPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    
    // Check total photo limit
    const totalPhotos = editPhotos.length + newPhotos.length + imageFiles.length
    if (totalPhotos > 5) {
      alert('Maximum 5 photos allowed')
      return
    }

    // Create previews
    const previews: string[] = []
    imageFiles.forEach(file => {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          previews.push(e.target.result as string)
          if (previews.length === imageFiles.length) {
            setNewPhotoPreviews([...newPhotoPreviews, ...previews])
          }
        }
      }
      reader.readAsDataURL(file)
    })

    setNewPhotos([...newPhotos, ...imageFiles])
  }

  const handleRemoveExistingPhoto = (index: number) => {
    setEditPhotos(editPhotos.filter((_, i) => i !== index))
  }

  const handleRemoveNewPhoto = (index: number) => {
    setNewPhotos(newPhotos.filter((_, i) => i !== index))
    setNewPhotoPreviews(newPhotoPreviews.filter((_, i) => i !== index))
  }

  const handleDelete = async () => {
    if (!onDelete || !confirm('Are you sure you want to delete this report?')) return

    setIsSubmitting(true)
    try {
      await onDelete(poi.id)
      onClose()
    } catch (error) {
      console.error('Failed to delete POI:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResolve = async (resolved: boolean) => {
    if (!onResolve) return

    setIsSubmitting(true)
    try {
      await onResolve(poi.id, resolved)
    } catch (error) {
      console.error('Failed to update POI:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp || !timestamp.toDate) return 'Unknown'
    return timestamp.toDate().toLocaleDateString()
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className={embedMode ? 
          "absolute inset-0 bg-black/20 z-40" : 
          "fixed inset-0 bg-black/20 z-40"
        } 
        onClick={onClose}
      />
      
      {/* Compact Modal */}
      <div 
        className={embedMode ? 
          "absolute bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 flex flex-col overflow-hidden" :
          "fixed bg-gray-800 rounded-xl shadow-2xl border border-gray-700 z-50 flex flex-col overflow-hidden"
        }
        style={{ 
          transform: `scale(${viewportScale})`,
          transformOrigin: 'top right',
          right: embedMode ? '48px' : '16px',
          top: embedMode ? '80px' : '80px',
          width: '384px',
          maxHeight: 'calc(100vh - 160px)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div 
              className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center"
              style={{ 
                backgroundColor: poi.resolved ? '#37415120' : `${poiType.color}20`,
                border: `1px solid ${poi.resolved ? '#6b7280' : poiType.color}` 
              }}
            >
              <Icon 
                className="w-4 h-4" 
                style={{ color: poi.resolved ? '#6b7280' : poiType.color }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-white truncate">
                {isEditing ? 'Edit Report' : poi.name}
              </h3>
              <p className="text-sm text-gray-400">{poiType.name} Report</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {isEditing ? (
              <>
                {/* Edit Form */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    maxLength={50}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    rows={4}
                  />
                </div>

                {/* Photo Management */}
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">
                    Photos
                  </label>
                  
                  {/* Existing Photos */}
                  {editPhotos.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">Existing photos</p>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {editPhotos.map((photo, index) => (
                          <div key={`existing-${index}`} className="relative group">
                            <img
                              src={photo}
                              alt={`Existing ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingPhoto(index)}
                              className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* New Photos */}
                  {newPhotoPreviews.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-2">New photos</p>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        {newPhotoPreviews.map((preview, index) => (
                          <div key={`new-${index}`} className="relative group">
                            <img
                              src={preview}
                              alt={`New ${index + 1}`}
                              className="w-full h-20 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveNewPhoto(index)}
                              className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add Photos Button */}
                  {editPhotos.length + newPhotos.length < 5 && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAddNewPhotos}
                        className="hidden"
                        id="edit-photo-input"
                      />
                      <label
                        htmlFor="edit-photo-input"
                        className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors text-sm cursor-pointer"
                      >
                        <Camera className="w-4 h-4" />
                        Add Photos ({editPhotos.length + newPhotos.length}/5)
                      </label>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Description */}
                <div>
                  <p className="text-gray-300 text-sm leading-relaxed">{poi.description}</p>
                </div>

                {/* Photos */}
                {poi.photos && poi.photos.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photos ({poi.photos.length})
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      {poi.photos.map((photo, index) => (
                        <div key={index} className="relative group cursor-pointer">
                          <img
                            src={photo}
                            alt={`${poi.name} - Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                            onClick={() => window.open(photo, '_blank')}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}


                {/* Quick Info */}
                <div className="flex items-center gap-4 text-sm">
                  {/* Author */}
                  <div className="flex items-center gap-2">
                    {poi.authorPhoto && (
                      <img 
                        src={poi.authorPhoto} 
                        alt={poi.authorName}
                        className="w-6 h-6 rounded-full"
                      />
                    )}
                    <span className="text-gray-300">{poi.authorName}</span>
                  </div>
                  
                  {/* Date */}
                  <span className="text-gray-400">{formatDate(poi.createdAt)}</span>
                </div>

                {/* Expiry Info */}
                {poi.expiresAt && (
                  <div className="flex items-center gap-2 text-sm text-amber-500">
                    <Clock className="w-4 h-4" />
                    <span>Valid until {formatDate(poi.expiresAt)}</span>
                  </div>
                )}

                {/* Status */}
                {poi.resolved && (
                  <div className="p-3 bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-300 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      This report has been resolved
                    </p>
                    {poi.resolvedAt && (
                      <p className="text-xs text-gray-500 mt-1">
                        Resolved on {formatDate(poi.resolvedAt)}
                      </p>
                    )}
                  </div>
                )}

                {/* Confirmation Status */}
                {totalVotes > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-gray-300">Community Feedback</h3>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-300">{confirmationCount} Confirmed</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-500" />
                        <span className="text-sm text-gray-300">{outdatedCount} Marked Outdated</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Vote History */}
                {(confirmationCount > 0 || outdatedCount > 0) && (
                  <div className="border-t border-gray-700 pt-4 mt-4">
                    <UserPOIVoteHistory
                      confirmations={poi.confirmations || {}}
                      outdatedVotes={poi.outdatedVotes || {}}
                    />
                  </div>
                )}

                {/* Comments Section */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                  <UserPOICommentsList
                    comments={comments}
                    currentUserId={currentUserId}
                    onAddComment={addComment}
                    onEditComment={editComment}
                    onDeleteComment={removeComment}
                    loading={commentsLoading}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-700 space-y-3">
          {isEditing ? (
            <div className="flex gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50"
                disabled={isSubmitting || !editName.trim() || !editDescription.trim()}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <>
              {/* User Actions */}
              {currentUserId && !poi.resolved && (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      if (hasConfirmed) {
                        // Already confirmed, clicking again removes vote
                        onRemoveVote?.(poi.id)
                      } else {
                        // Not confirmed (may be outdated), confirm it
                        onConfirm?.(poi.id)
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors ${
                      hasConfirmed 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-gray-700 hover:bg-green-700'
                    }`}
                    disabled={isSubmitting}
                  >
                    <CheckCircle className="w-4 h-4" />
                    {hasConfirmed ? 'Confirmed' : 'Confirm'}
                  </button>
                  <button
                    onClick={() => {
                      if (hasMarkedOutdated) {
                        // Already marked outdated, clicking again removes vote
                        onRemoveVote?.(poi.id)
                      } else {
                        // Not marked outdated (may be confirmed), mark it
                        onMarkOutdated?.(poi.id)
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg transition-colors ${
                      hasMarkedOutdated 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-gray-700 hover:bg-red-700'
                    }`}
                    disabled={isSubmitting}
                  >
                    <Clock className="w-4 h-4" />
                    {hasMarkedOutdated ? 'Marked Outdated' : 'Outdated'}
                  </button>
                </div>
              )}

              {/* Author Actions */}
              {isAuthor && (
                <div className="flex gap-2">
                  {!poi.resolved && (
                    <>
                      <button
                        onClick={() => {
                          setEditName(poi.name)
                          setEditDescription(poi.description)
                          setEditPhotos(poi.photos || [])
                          setIsEditing(true)
                        }}
                        className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      {onStartMove && (
                        <button
                          onClick={() => {
                            onClose()
                            onStartMove(poi.id)
                          }}
                          className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                        >
                          <Move className="w-3.5 h-3.5" />
                          Move
                        </button>
                      )}
                      <button
                        onClick={() => handleResolve(true)}
                        className="flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm"
                        disabled={isSubmitting}
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Resolved
                      </button>
                    </>
                  )}
                  {poi.resolved && (
                    <button
                      onClick={() => handleResolve(false)}
                      className="flex-1 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                      disabled={isSubmitting}
                    >
                      Reopen Report
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    disabled={isSubmitting}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}